import VastParser from '@ogury/vast-library/parser';
import { logMessage } from '../../../../common/js/sentry';
import { EndScreenType } from '../end-screen/end-screen-types';

// We set priorities here
// But there is also a priority like in the method _selectBestEndScreenByCompanion
// If you change it reminds that the priorities corresponds also to the steps
// When I wrote this comment the priorities were :
// 1. Static resources
// 2. Html Resources
// 3. Iframe Resources
const typePriorities: Record<string, number> = {
  gif: 10,
  jpeg: 8,
  png: 6,
  html: 4,
  iframe: 2
};

interface VastElement {
  name: string;
  content: string;
  childs: VastElement[];
  attrs: Record<string, string>;
  getAttr: (name: string) => string;
  getContent: () => string;
  getChilds: (name: string) => VastElement[];
}

interface EndScreen {
  content: string;
  priority: number;
  type: string;
  clickThrough?: string[] | null;
  clickTracking?: string[] | null;
}

export class VastEndScreenResolver {
  _vastParser: VastParser;

  constructor(vastParser: VastParser) {
    this._vastParser = vastParser;
  }

  resolve(): EndScreen | null {
    try {
      const path = ['CompanionAds', 'Companion'];
      const companions = this._vastParser.getVastElements(path);
      if (this._isEmptyProperty(companions)) return null;
      return companions.reduce<EndScreen | null>(
        (acc, companion) => this._companionReducer(acc, companion as any),
        null
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      logMessage('VAST end screen ad companion parser error', { message });
      return null;
    }
  }

  _companionReducer(
    bestEndScreen: EndScreen | null,
    companion: VastElement
  ): EndScreen | null {
    if (this._isEmptyProperty(companion.childs)) return null;

    const endScreen = this._selectBestEndScreenByCompanion(companion);
    if (endScreen === null) return bestEndScreen;

    if (bestEndScreen === null || endScreen.priority > bestEndScreen.priority) {
      const clickThrough = this._getClickThrough(companion);
      const clickTracking = this._getClickTracking(companion);
      return { ...endScreen, clickThrough, clickTracking };
    }

    return bestEndScreen;
  }

  _selectBestEndScreenByCompanion(
    companion: VastElement
  ): Omit<EndScreen, 'clickThrough' | 'clickTracking'> | null {
    const steps = [
      this._selectStaticEndScreen.bind(this),
      this._selectHtmlEndScreen.bind(this),
      this._selectIframeEndScreen.bind(this)
    ];
    for (let i = 0; i < steps.length; i++) {
      const endScreen = steps[i](companion);
      if (endScreen) return endScreen;
    }
    return null;
  }

  _selectStaticEndScreen(
    companion: VastElement
  ): Omit<EndScreen, 'clickThrough' | 'clickTracking'> | null {
    const endScreen = this._generateStaticEndScreensSorted(companion)[0];
    if (!endScreen || endScreen.priority === 0) return null;
    const { content, priority } = endScreen;
    return {
      content,
      priority,
      type: EndScreenType.IMAGE
    };
  }

  _generateStaticEndScreensSorted(
    companion: VastElement
  ): { content: string; priority: number }[] {
    const endScreens = companion.childs
      .filter(({ name }) => name === 'StaticResource')
      .map(({ content, attrs: { creativeType } }) => {
        const type = creativeType && creativeType.split('/');
        const priority = (type && type[1] && typePriorities[type[1]]) || 0;
        return { content, priority };
      })
      .sort((a, b) => b.priority - a.priority);
    return endScreens;
  }

  _selectHtmlEndScreen(
    companion: VastElement
  ): Omit<EndScreen, 'clickThrough' | 'clickTracking'> | null {
    return this._selectFirstEndScreen({
      companion,
      tag: 'HTMLResource',
      type: EndScreenType.HTML
    });
  }

  _selectIframeEndScreen(
    companion: VastElement
  ): Omit<EndScreen, 'clickThrough' | 'clickTracking'> | null {
    return this._selectFirstEndScreen({
      companion,
      tag: 'IFrameResource',
      type: EndScreenType.IFRAME
    });
  }

  _selectFirstEndScreen({
    companion,
    tag,
    type
  }: {
    companion: VastElement;
    tag: string;
    type: string;
  }): Omit<EndScreen, 'clickThrough' | 'clickTracking'> | null {
    const tags = this._getTags({ companion, tag });
    if (tags == null) return null;
    const content = tags[0];
    return { content, type, priority: typePriorities[type] };
  }

  _getClickThrough(companion: VastElement): string[] | null {
    return this._getTags({ companion, tag: 'CompanionClickThrough' });
  }

  _getClickTracking(companion: VastElement): string[] | null {
    return this._getTags({ companion, tag: 'CompanionClickTracking' });
  }

  _getTags({
    companion,
    tag
  }: {
    companion: VastElement;
    tag: string;
  }): string[] | null {
    const tags = companion.childs
      .filter(({ name }) => name === tag)
      .map(({ content }) => content);
    if (this._isEmptyProperty(tags)) return null;
    return tags;
  }

  _isEmptyProperty(vastProperty: any[] | null | undefined): boolean {
    return !vastProperty || !vastProperty.length || !vastProperty[0];
  }
}
